import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Mic, Settings, Video, Volume2 } from '@/shared/ui/icons';
import { Button } from '../../shared/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../shared/ui/dropdown-menu';
import { UserAvatar } from '../../shared/ui/user-avatar';
import type { HuddleDeviceSelection, HuddleDeviceSource } from './HuddleMedia';
import type { useHuddlePreview } from './useHuddlePreview';
import type { useHuddleSpeakerTest } from './useHuddleSpeakerTest';
import type { HuddleCameraPreferences } from './HuddleCamera';

export interface HuddlePreviewIdentity { name?: string; avatar?: string | null }

export function previewError(source: HuddleDeviceSource, error: string): string {
  const label = source === 'microphone' ? 'microphone' : 'camera';
  if (error === 'NotAllowedError' || error === 'SecurityError') return `Allow ${label} access in your browser settings, then turn it on again. You can join with it off.`;
  if (error === 'NotFoundError' || error === 'OverconstrainedError') return `This ${label} was not found. Choose another device or connect it and try again.`;
  if (error === 'NotReadableError') return `This ${label} is in use by another app. Close that app and try again.`;
  if (error === 'DeviceDisconnected') return `The ${label} disconnected. Choose a connected device and try again.`;
  if (error === 'NotSupportedError') return `This browser cannot preview the ${label}. Check that you are using a secure browser connection.`;
  return `The ${label} could not start. Check the device and turn it on again, or join with it off.`;
}

function CameraPreview({ stream, mirrorPreview }: { stream: MediaStream; mirrorPreview: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    let disposed = false;
    setBlocked(false); element.srcObject = stream;
    void element.play().catch(() => { if (!disposed) setBlocked(true); });
    return () => { disposed = true; element.pause(); element.srcObject = null; };
  }, [stream]);
  return <>
    <video ref={video} autoPlay muted playsInline aria-label="Your camera preview" className="qs-huddle-preview-video" style={{ transform: mirrorPreview ? 'scaleX(-1)' : undefined }} />
    {blocked && <Button className="absolute left-3 top-3" size="sm" onClick={() => { void video.current?.play().then(() => setBlocked(false)).catch(() => setBlocked(true)); }}>Play preview</Button>}
  </>;
}

export function HuddlePreviewStage({ stream, identity, children, mirrorPreview = true }: { stream?: MediaStream; identity?: HuddlePreviewIdentity; children: ReactNode; mirrorPreview?: boolean }) {
  return <section aria-label="Your huddle preview" className="qs-huddle-preview-stage">
    {stream ? <CameraPreview stream={stream} mirrorPreview={mirrorPreview} /> : <div className="qs-huddle-preview-identity">
      <UserAvatar src={identity?.avatar} name={identity?.name || 'You'} size={144} borderRadius={24} fallback="initials" />
      <span>{identity?.name || 'You'}</span>
    </div>}
    <span className="qs-huddle-preview-you">You</span>
    {children}
  </section>;
}

export function HuddleDevicePreview({ source, preview, speaker, devices, onDevicesChange, disabled, cameraPreferences, onCameraSettings }: {
  source: HuddleDeviceSource | 'speaker';
  preview: ReturnType<typeof useHuddlePreview>;
  speaker: ReturnType<typeof useHuddleSpeakerTest>;
  devices: HuddleDeviceSelection;
  onDevicesChange: (devices: HuddleDeviceSelection) => void;
  disabled: boolean;
  cameraPreferences?: HuddleCameraPreferences;
  onCameraSettings?: () => void;
}) {
  const label = source === 'microphone' ? 'Microphone' : source === 'camera' ? 'Camera' : 'Speaker';
  const kind = source === 'microphone' ? 'audioinput' : source === 'camera' ? 'videoinput' : 'audiooutput';
  const available = preview.state.devices.filter(device => device.kind === kind && device.deviceId && device.deviceId !== 'default' && device.deviceId !== 'communications');
  const selected = devices[source];
  const selectedLabel = selected ? available.find(device => device.deviceId === selected)?.label || `Selected ${label.toLowerCase()}`
    : preview.state.devices.find(device => device.kind === kind && device.deviceId === 'default')?.label || 'System default';
  const Icon = source === 'microphone' ? Mic : source === 'camera' ? Video : Volume2;
  const busy = source !== 'speaker' && preview.state[source].busy;
  return <DropdownMenu onOpenChange={open => { if (open) void preview.refreshDevices(); }}>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" aria-label={`${label} device`} title={`${label}: ${selectedLabel}`} disabled={disabled || busy} className="qs-huddle-device-trigger">
        <Icon className="size-4 shrink-0" /><span className="truncate">{selectedLabel}</span><ChevronDown className="size-3 shrink-0" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="center" className="qs-huddle-device-menu w-72 max-w-[calc(100vw-32px)]">
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuRadioGroup value={selected || 'default'} onValueChange={device => {
        const next = device === 'default' ? undefined : device;
        onDevicesChange({ ...devices, [source]: next });
        if (source === 'speaker') speaker.stop();
        else if (preview.state[source].stream) void preview.start(source, next, source === 'camera' ? { ...cameraPreferences, controls: {} } : undefined);
      }}>
        <DropdownMenuRadioItem value="default">System default</DropdownMenuRadioItem>
        {available.map((device, index) => <DropdownMenuRadioItem key={device.deviceId} value={device.deviceId} disabled={source === 'speaker' && !speaker.supported}>
          {device.label || `${label} ${index + 1}`}
        </DropdownMenuRadioItem>)}
      </DropdownMenuRadioGroup>
      {source === 'speaker' && <>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={event => { event.preventDefault(); if (speaker.testing) speaker.stop(); else void speaker.start(devices.speaker); }}>
          <Volume2 className="size-4" />{speaker.testing ? 'Stop test' : 'Test speaker'}
        </DropdownMenuItem>
        {!speaker.supported && <p className="px-2 py-1 text-xs text-muted-foreground">This browser uses your system’s speaker. Change it in system sound settings.</p>}
      </>}
      {source === 'camera' && onCameraSettings && <>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onCameraSettings}><Settings className="size-4" />Camera settings</DropdownMenuItem>
      </>}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => { void preview.refreshDevices(); }}>Refresh devices</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>;
}
